/**
 * The company profile, and the public branding the sign-in screen uses.
 *
 * Two queries against overlapping data, deliberately. `useCompany` is the whole
 * profile and needs a session; `useBranding` is the name, tagline and logo and needs
 * nothing, because the sign-in screen has to render before anybody has signed in.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { BrandingRead, CompanyRead, CompanyUpdate } from '@/api/schema';
import { asDateFormat, type DateFormat } from '@/lib/dates';
import { apiUrl } from '@/lib/env';

export const COMPANY_KEY = ['company'] as const;
export const BRANDING_KEY = ['company', 'branding'] as const;

/**
 * The logo's address, or null when there is none.
 *
 * Absolute, via `apiUrl`, because this goes into an `<img src>` rather than through
 * the fetch client. The API is a separate origin, so a root-relative path would ask
 * the frontend's own server for it.
 *
 * Versioned on `updated_at`: the image sits at one fixed address, so without it a
 * replaced logo would come straight back out of the browser cache.
 */
export function logoUrl(branding: BrandingRead | undefined): string | null {
  if (!branding?.has_logo) return null;
  return `${apiUrl('/api/v1/company/logo')}?v=${encodeURIComponent(branding.updated_at)}`;
}

export function useCompany() {
  return useQuery({
    queryKey: COMPANY_KEY,
    queryFn: () => api.get<CompanyRead>('/api/v1/company'),
  });
}

export function useBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<BrandingRead>('/api/v1/company/branding'),
    // The name on the sign-in screen and in the sidebar changes about never, and a
    // refetch on every window focus would be a request per tab switch for it.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Everything that changes the profile invalidates both queries.
 *
 * The branding is a projection of the same row, so leaving it alone would show the
 * old name in the sidebar until something else happened to refetch it.
 */
function useCompanyMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<CompanyRead>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: async (updated) => {
      queryClient.setQueryData(COMPANY_KEY, updated);
      await queryClient.invalidateQueries({ queryKey: BRANDING_KEY });
    },
  });
}

export function useUpdateCompany() {
  return useCompanyMutation((changes: CompanyUpdate) =>
    api.patch<CompanyRead>('/api/v1/company', changes),
  );
}

export function useUploadLogo() {
  return useCompanyMutation((file: File) => {
    const body = new FormData();
    body.append('file', file);
    return api.upload<CompanyRead>('/api/v1/company/logo', body);
  });
}

export function useRemoveLogo() {
  return useCompanyMutation(() => api.delete<CompanyRead>('/api/v1/company/logo'));
}

/**
 * The company's date format, ready to hand to `formatDate`.
 *
 * The format is company-wide and the clock is always the viewer's own (see
 * `lib/dates`). Every screen that renders a date needs both, so the lookup lives here
 * rather than being copied into each one.
 *
 * It costs nothing after the first call: `useCompany` is a cached query, and the
 * profile is already loaded by the time any list screen is on screen.
 */
export function useDateFormat(): DateFormat {
  const company = useCompany();
  return asDateFormat(company.data?.date_format);
}
